import { trackMiscAction, trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import ShareFrameModal from 'components/Modal/Share/ShareFrameModal';
import InspectorLeftPanel from 'components/Panel/InspectorLeftPanel';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import {
	Navbar,
	NavbarLeft,
	NavbarCenter,
	NavbarRight,
	NavbarButton,
	ToolButton,
	NavbarTitle,
} from 'components/Widget/Navbar';
import { DEFAULT_DEVICES, Device, DomElement } from 'dragonmouse-shared/interfaces';
import { SelectionInfo, Tool } from 'dragonmouse-shared/devigner';
import { Point } from 'dragonmouse-shared/geometry';
import { getActiveVisualEditsHash } from 'dragonmouse-shared/util';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
	selectActiveScene,
	selectFrame,
	selectIsFreeTier,
	selectUser,
	selectUserSettings,
} from 'redux/db/reducers';
import { Direction, getAdjacentFrame, getFrameExportHtml, getSelectionSvg } from 'redux/db/util';
import { setPrototypeScaling, setSelectedDomElements, setSidebarVisible } from 'redux/mode/actions';
import { displayUpgradeModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { downloadBlob, frameSiteUrl } from 'utils';
import Prototype from './Prototype';
import * as S from './styles';

interface FrameInspectorProps {
	frameId: string;
}

const FrameInspector = ({ frameId }: FrameInspectorProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showShareModal, setShowShareModal] = useState(false);
	const [panelsVisible, setPanelsVisible] = useState(true);
	const [activeTool, setActiveTool] = useState<Tool | null>(null);
	const [iframeReady, setIframeReady] = useState(false);
	const [contextMenuOpen, setContextMenuOpen] = useState(false);
	const [selectionInfo, setSelectionInfo] = useState<SelectionInfo | null>(null);
	const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
		null
	);
	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [prototypeFrameSize, setPrototypeFrameSize] = useState({ width: 0, height: 0 });
	const [iframeReloadKey, setIframeReloadKey] = useState(0);

	const { scene, frame, prototypeScaling, isDragging, visualEditsEnabled, user, isFreeTier } =
		useSelector((r: RootReducer) => {
			return {
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				frame: selectFrame(r.db, frameId),
				prototypeScaling: r.mode.prototypeScaling,
				isDragging: r.mode.isDragging,
				visualEditsEnabled: selectUserSettings(r.db).visualEditsEnabled,
				user: selectUser(r.db),
				isFreeTier: selectIsFreeTier(r.db),
			};
		}, shallowEqual);
	const siteUrl = frame ? frameSiteUrl(frame) : null;

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	const exit = () => {
		if (scene) {
			dispatch(setSidebarVisible(false));
			navigate(routes.scene(scene.id, scene.title));
		} else {
			console.error('FrameInspector: cannot access scene');
		}
	};

	const openInNewTab = useCallback(() => {
		if (!siteUrl) return;

		const url = new URL(siteUrl);

		// add veHash if visual edits are active
		const veHash = frame ? getActiveVisualEditsHash(frame) : null;

		if (veHash) {
			url.searchParams.append('veHash', veHash);
		}
		window.open(url.toString(), '_blank', 'noopener,noreferrer');
	}, [siteUrl, frame]);

	const handleDevicePreset = (device: Device) => {
		setPrototypeFrameSize({ width: device.width, height: device.height });
	};

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		// here's a cleanup function to reset selected element ids when component unmounts
		return () => {
			dispatch(setSelectedDomElements([]));
		};
	}, [dispatch]);

	// Initialize prototype frame size when frame changes
	useEffect(() => {
		if (frame) {
			setPrototypeFrameSize({ width: frame.width, height: frame.height });
		}
	}, [frame?.width, frame?.height]);

	const onSelectElement = (element: DomElement) => {
		dispatch(setSelectedDomElements([element]));
	};

	//------------------------------------------------------------------------------------------------
	// NAVIGATION LOGIC
	//------------------------------------------------------------------------------------------------
	const navigateToFrame = (targetFrameId: string | null) => {
		if (targetFrameId && scene) {
			navigate(routes.frame(scene.id, scene.title, targetFrameId));
		}
	};

	const onArrowLeft = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.LEFT);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowRight = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.RIGHT);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowUp = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.UP);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	const onArrowDown = () => {
		if (frame?.id) {
			const targetFrame = getAdjacentFrame(frame.id, Direction.DOWN);
			navigateToFrame(targetFrame?.id || null);
		}
	};

	// Check if navigation is available in each direction
	const canNavigateLeft = frame?.id ? !!getAdjacentFrame(frame.id, Direction.LEFT) : false;
	const canNavigateRight = frame?.id ? !!getAdjacentFrame(frame.id, Direction.RIGHT) : false;
	const canNavigateUp = frame?.id ? !!getAdjacentFrame(frame.id, Direction.UP) : false;
	const canNavigateDown = frame?.id ? !!getAdjacentFrame(frame.id, Direction.DOWN) : false;

	// Handle global Escape key to clear selection and close context menu
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			const consumesArrowKeys =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable ||
				target.closest('input, textarea, [contenteditable="true"]');

			if (e.key === 'Escape') {
				setActiveTool(null);
				dispatch(setSelectedDomElements([]));
				setContextMenuOpen(false);
				setSelectionInfo(null);
			} else if (!consumesArrowKeys) {
				// Only handle arrow keys when not in an editable element
				if (e.key === 'ArrowLeft' && canNavigateLeft) {
					e.preventDefault();
					onArrowLeft();
				} else if (e.key === 'ArrowRight' && canNavigateRight) {
					e.preventDefault();
					onArrowRight();
				} else if (e.key === 'ArrowUp' && canNavigateUp) {
					e.preventDefault();
					onArrowUp();
				} else if (e.key === 'ArrowDown' && canNavigateDown) {
					e.preventDefault();
					onArrowDown();
				}
			}
		};

		// the third parameter here ('true') seems needed to capture all keyboard events
		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, [
		canNavigateLeft,
		canNavigateRight,
		canNavigateUp,
		canNavigateDown,
		onArrowLeft,
		onArrowRight,
		onArrowUp,
		onArrowDown,
		dispatch,
	]);

	const handleDownloadHtml = useCallback(async () => {
		if (!frame) return;

		if (isFreeTier) {
			displayUpgradeModal('HTML export');
			return;
		}

		trackOutputAction('export_html');

		const blobPromise = getFrameExportHtml(frame);
		toast.promise(blobPromise, {
			pending: 'Exporting HTML...',
			success: 'Exported HTML',
			error: 'Failed to export HTML',
		});

		downloadBlob(await blobPromise, `${frame.title || 'untitled'}.html`);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		user?.id &&
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						numberOfExports: numberOfExports + 1,
						lastExportDate: new Date().toISOString(),
					},
				},
			});
	}, [frame, isFreeTier, user]);

	const moreMenuItems = useMemo(() => {
		if (!frame) return [];

		return [
			new MenuItemAction(
				fullscreenMode ? 'Switch to fixed aspect ratio view' : 'Switch to fullscreen view',
				'',
				() => setFullscreenMode(!fullscreenMode),
				!siteUrl,
				fullscreenMode
			),
			new MenuItemAction(
				prototypeScaling === 'actual-size' ? 'Switch to mockup view' : 'Switch to actual size view',
				'',
				() =>
					dispatch(
						setPrototypeScaling(prototypeScaling === 'actual-size' ? 'mockup' : 'actual-size')
					),
				false,
				prototypeScaling === 'actual-size'
			),
			new MenuItemAction('Open in new tab', '', openInNewTab, !siteUrl),
			new MenuItemAction('Download HTML', '', handleDownloadHtml, !siteUrl),
		];
	}, [
		frame,
		fullscreenMode,
		prototypeScaling,
		siteUrl,
		dispatch,
		openInNewTab,
		handleDownloadHtml,
	]);

	//------------------------------------------------------------------------------------------------
	// EARLY EXIT (must be defined after all hooks)
	//------------------------------------------------------------------------------------------------

	if (!frame) return null;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onContextMenu = (selectionInfo: SelectionInfo, point: Point) => {
		if (!frame) return;
		// Use the position data that's already calculated in the Prototype component
		setContextMenuPosition(point);
		setSelectionInfo(selectionInfo);
		setContextMenuOpen(true);
	};

	const handleExportClick = async () => {
		if (!selectionInfo || !frame?.siteId) return;

		try {
			const svgBlob = await getSelectionSvg(selectionInfo, frame.siteId);
			if (svgBlob) {
				const svgText = await svgBlob.text();
				await navigator.clipboard.writeText(svgText);
				toast.success('SVG copied to clipboard');
			}
		} catch (error) {
			toast.error('Failed to copy SVG');
		}
		setContextMenuOpen(false);
		setSelectionInfo(null);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderCenterControls = () => {
		if (!siteUrl) {
			return (
				<>
					<Tooltip placement="bottom" id="preview-tooltip" />
					<S.SelectButton
						active={true}
						disabled={!iframeReady || !siteUrl}
						onClick={() => {}}
						data-tooltip-id="preview-tooltip"
						data-tooltip-content={'Imported images can be previewed, but not edited'}
					>
						<Icon type={IconType.PLAY} width={16} height={16} />
						Preview
					</S.SelectButton>
				</>
			);
		}

		if (!visualEditsEnabled) {
			return (
				<>
					<Tooltip placement="bottom" id="select-tooltip" />
					<S.SelectButton
						active={activeTool === 'select'}
						disabled={!iframeReady || !siteUrl}
						onClick={() => {
							if (activeTool === 'select') {
								setActiveTool(null);
							} else {
								setActiveTool('select');
							}
							dispatch(setSelectedDomElements([]));
						}}
						data-tooltip-id="select-tooltip"
						data-tooltip-content={
							iframeReady
								? 'Activate selection mode (click on frame elements to add them to chat)'
								: 'Prototype code is loading, please wait...'
						}
					>
						<Icon type={IconType.SELECT} width={16} height={16} />
						Select
					</S.SelectButton>
				</>
			);
		}

		// visual edits are enabled
		return (
			<>
				<ToolButton
					icon={IconType.PLAY}
					active={activeTool === null}
					onClick={() => {
						setActiveTool(null);
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Preview' : 'Prototype code is loading, please wait...'}
					tooltipId="preview-tooltip"
				/>
				<S.ToolButtonSpacer />
				<ToolButton
					icon={IconType.SELECT}
					active={activeTool === 'select'}
					disabled={!iframeReady}
					onClick={() => {
						setActiveTool('select');
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Select tool' : 'Prototype code is loading, please wait...'}
					tooltipId="select-tooltip"
				/>
				<ToolButton
					icon={IconType.TEXT_2}
					active={activeTool === 'text'}
					disabled={!iframeReady}
					onClick={() => {
						setActiveTool('text');
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Text tool' : 'Prototype code is loading, please wait...'}
					tooltipId="text-edit-tooltip"
				/>
				<ToolButton
					icon={IconType.IMAGE}
					active={activeTool === 'image'}
					disabled={!iframeReady}
					onClick={() => {
						setActiveTool('image');
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Image tool' : 'Prototype code is loading, please wait...'}
					tooltipId="image-tool-tooltip"
				/>
			</>
		);
	};
	return (
		<S.Base panelsVisible={panelsVisible}>
			{/*TODO -- decide if we need this.  I think this is left over from v1.  Research and decide before merging
			 {imageUrl && <S.ImagePreloader src={imageUrl} />} */}
			<S.NavbarWrapper>
				<Navbar>
					<NavbarLeft>
						{scene && <NavbarButton icon={IconType.LINE_ARROW_LEFT} onClick={exit} />}
						<NavbarButton
							icon={IconType.MAXIMIZE}
							active={panelsVisible}
							onClick={() => {
								panelsVisible
									? trackMiscAction('close_inspector_side_panel')
									: trackMiscAction('open_inspector_side_panel');
								setPanelsVisible(!panelsVisible);
							}}
							tooltip={panelsVisible ? 'Close side panel' : 'Open side panel'}
							tooltipId="side-panel-tooltip"
						/>
						<NavbarButton
							icon={IconType.REFRESH}
							onClick={() => {
								setIframeReloadKey((prev) => prev + 1);
							}}
							disabled={!siteUrl}
							tooltip={
								!siteUrl ? 'Reload is not available for imported images' : 'Reload prototype'
							}
							tooltipId="reload-tooltip"
						/>
						<NavbarTitle>{frame.title}</NavbarTitle>
					</NavbarLeft>

					<NavbarCenter>{renderCenterControls()}</NavbarCenter>

					<NavbarRight>
						{/* Device Preset Buttons */}
						{DEFAULT_DEVICES.map((device) => {
							const isActive =
								prototypeFrameSize.width === device.width &&
								prototypeFrameSize.height === device.height;
							const tooltipId = `device-${device.type}-tooltip`;

							return (
								<NavbarButton
									key={device.type}
									icon={
										device.type === 'mobile'
											? IconType.MOBILE
											: device.type === 'tablet'
											? IconType.TABLET
											: IconType.DESKTOP
									}
									active={isActive}
									disabled={!siteUrl}
									onClick={() => handleDevicePreset(device)}
									tooltip={
										!siteUrl
											? 'Device presets are not available for imported images'
											: `Set to ${device.type} (${device.width}×${device.height})`
									}
									tooltipId={tooltipId}
								/>
							);
						})}

						<DropdownMenu menuItems={moreMenuItems}>
							<S.DropdownTrigger
								data-tooltip-id="more-menu-tooltip"
								data-tooltip-content="More options"
							>
								<Icon type={IconType.MORE2} width={16} height={16} />
							</S.DropdownTrigger>
						</DropdownMenu>
						<Tooltip id="more-menu-tooltip" placement="bottom" />
						<S.Share>
							<Button
								onSelected={() => {
									trackMiscAction('open_frame_share_modal');
									setShowShareModal(true);
								}}
								type={ButtonType.NOTICEABLE}
							>
								Share
							</Button>
						</S.Share>
					</NavbarRight>
				</Navbar>
			</S.NavbarWrapper>
			<S.PrototypeArea>
				<Prototype
					frame={frame}
					siteUrl={siteUrl}
					isDragging={isDragging}
					activeTool={activeTool}
					fullscreenMode={fullscreenMode}
					onSelectElement={onSelectElement}
					onSetActiveTool={setActiveTool}
					onSetSelectedElements={(elements) => dispatch(setSelectedDomElements(elements))}
					onIframeReadyChange={setIframeReady}
					onContextMenu={onContextMenu}
					prototypeFrameSize={prototypeFrameSize}
					onSetFrameSize={setPrototypeFrameSize}
					iframeReloadKey={iframeReloadKey}
				/>
			</S.PrototypeArea>
			<S.Footer>
				<S.FooterActions>
					<S.FooterButton
						onClick={onArrowLeft}
						disabled={!canNavigateLeft}
						title="Navigate left (left arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_LEFT} width={11} height={11} />
					</S.FooterButton>
					<S.FooterButton
						onClick={onArrowRight}
						disabled={!canNavigateRight}
						title="Navigate right (right arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={11} height={11} />
					</S.FooterButton>
					<S.FooterSpacer />
					<S.FooterButton
						onClick={onArrowUp}
						disabled={!canNavigateUp}
						title="Navigate up (up arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_UP} width={11} height={11} />
					</S.FooterButton>
					<S.FooterButton
						onClick={onArrowDown}
						disabled={!canNavigateDown}
						title="Navigate down (down arrow key)"
					>
						<Icon type={IconType.LINE_ARROW_DOWN} width={11} height={11} />
					</S.FooterButton>
				</S.FooterActions>
				<S.DimensionsDisplay>
					Frame size: {Math.round(prototypeFrameSize.width)} ×{' '}
					{Math.round(prototypeFrameSize.height)}
				</S.DimensionsDisplay>
			</S.Footer>
			{panelsVisible && (
				<InspectorLeftPanel frameId={frameId} prototypeWidth={prototypeFrameSize.width} />
			)}
			{showShareModal && (
				<ShareFrameModal frameId={frame.id} onClose={() => setShowShareModal(false)} />
			)}
			{contextMenuOpen && contextMenuPosition && (
				<S.ContextMenuWrapper x={contextMenuPosition.x} y={contextMenuPosition.y} isVisible={true}>
					<S.ContextMenu>
						<S.Items onClick={handleExportClick}>Copy selection as SVG</S.Items>
					</S.ContextMenu>
				</S.ContextMenuWrapper>
			)}
		</S.Base>
	);
};

export default FrameInspector;
